import { faArrowRight, faTags } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from '@supply-mover-2-0/web-ui';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { usePortalAuth } from '../../../hooks/usePortalAuth';
import * as dashboardService from '../../../services/portalDashboardService';
import { getStorefrontPromoItems } from '../../../services/portalDashboardService';
import { ItemCarousel } from './ItemCarousel';

/**
 * Promo + items combo — section header on top, "shop the sale" items
 * carousel underneath. Lets admins pair a campaign with the actual
 * on-sale items below the header (Weee/Instacart pattern). Replaces
 * the older pattern of a banner widget that just routes to the promo
 * detail page.
 *
 * Picks the promo by explicit `promoId` from `widgetConfig` — no rule-
 * based selection here; the admin curates which promo+items pair to
 * surface.
 *
 * Layout is intentionally light: section header (title + View All →
 * /promos/:id) on top, ItemCarousel of the promo's items beneath.
 * We previously rendered a `<PromoTile size="strip">` decorative band
 * between the two, but at the 16:1 / 24:1 strip aspect the art read
 * as a thin colored bar with little meaningful content, and the
 * placeholder gradient (when no bannerStripUrl was uploaded) was
 * visually duplicative of the section title above it. Dropping the
 * strip keeps the visual emphasis on the shoppable items themselves.
 */
export function PromoWithItemsWidget({
	title,
	promoId,
	limit = 8,
	density,
}: {
	title?: string;
	promoId: number | null;
	limit?: number;
	density?: 'standard' | 'compact';
}): JSX.Element | null {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { token } = usePortalAuth();

	const promoQueryKey = ['portal', 'promo-detail', promoId];
	const itemsQueryKey = ['portal', 'dashboard', 'promo-items', promoId, limit];

	const promoQuery = useQuery({
		queryKey: promoQueryKey,
		queryFn: () =>
			dashboardService.getStorefrontPromoDetail(promoId as number, token),
		enabled: promoId != null && Number.isFinite(promoId),
		staleTime: 5 * 60_000,
	});

	const itemsQuery = useQuery({
		queryKey: itemsQueryKey,
		queryFn: () => getStorefrontPromoItems(token, promoId as number, limit),
		enabled: promoId != null && Number.isFinite(promoId),
		staleTime: 5 * 60_000,
	});

	if (promoId == null) return null;
	if (promoQuery.isError) return null;

	// `getStorefrontPromoDetail` returns `{ promo, items, categoryNames }`
	// in `data`, not the promo directly — pulling `data.data` left
	// `imagesJson` undefined and PromoTile fell into its no-banner
	// gradient fallback.
	const promo = promoQuery.data?.data?.promo ?? null;
	const items = itemsQuery.data?.data ?? [];

	// Hide the whole row if the promo is gone or there are no shoppable
	// items to pair with the banner. A bare banner with no items is
	// already covered by promo-banner / promo-one-up; this widget's
	// reason to exist is the items beneath it.
	if (
		!promoQuery.isLoading &&
		!itemsQuery.isLoading &&
		(promo == null || items.length === 0)
	) {
		return null;
	}

	const headlineLabel = title ?? promo?.name ?? t('Shop the sale');

	return (
		<section className="w-full h-full flex flex-col gap-3">
			<header className="flex items-center justify-between">
				<h2 className="flex items-center gap-2 text-base md:text-lg font-semibold text-gray-900 dark:text-gray-100">
					<FontAwesomeIcon icon={faTags} className="text-portal-accent" />
					{t(headlineLabel)}
				</h2>
				{promo != null && (
					<button
						type="button"
						onClick={() => navigate(`/promos/${promo.id}`)}
						className="text-xs md:text-sm text-portal-accent hover:opacity-80 inline-flex items-center gap-1"
					>
						{t('View all')}
						<FontAwesomeIcon icon={faArrowRight} className="text-[10px]" />
					</button>
				)}
			</header>

			<ItemCarousel
				items={items}
				invalidateQueryKey={itemsQueryKey}
				density={density}
				emptyState={null}
			/>
		</section>
	);
}
